import { useState, useEffect, useMemo, useRef } from 'react';
import { Listbox, Combobox } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/24/solid';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { StackableModal, DatePicker } from '../components/nextgen';
import { AvatarGenerated } from '../components/Avatar';
import { getUserFullName, classNames } from '../components/helpers/functions';
import useModal from '../hooks/useModal';
import { createTask, createRecurrenceRule } from '../api/tasks';
import { getSopVersions } from '../api/sops';

const MODAL_NAME = 'createTask';

// Recurrence options
const RECURRENCE_OPTIONS = [
	{ value: null, label: 'No recurrence' },
	{ value: 'daily', label: 'Daily' },
	{ value: 'weekly', label: 'Weekly' },
	{ value: 'biweekly', label: 'Biweekly' },
	{ value: 'monthly', label: 'Monthly' },
	{ value: 'quarterly', label: 'Quarterly' },
	{ value: 'biyearly', label: 'Bi-yearly' },
	{ value: 'yearly', label: 'Yearly' },
];

// Reminder options (days before due date)
const REMINDER_OPTIONS = [
	{ value: null, label: 'No reminder' },
	{ value: 1, label: '1 day before' },
	{ value: 2, label: '2 days before' },
	{ value: 3, label: '3 days before' },
	{ value: 7, label: '1 week before' },
];

const CreateTaskModal = () => {
	const { open, setClose } = useModal(MODAL_NAME);

	const team = open?.team || [];
	const sops = open?.sops || [];
	const onSuccess = open?.onSuccess;

	// Form state
	const [title, setTitle] = useState('');
	const [content, setContent] = useState('');
	const [assignedTo, setAssignedTo] = useState(null);
	const [dueDate, setDueDate] = useState('');
	const [recurrence, setRecurrence] = useState(null);
	const [recurrenceEndDate, setRecurrenceEndDate] = useState('');
	const [reminderOffset, setReminderOffset] = useState(null);
	const [sopId, setSopId] = useState(null);
	const [sopVersionId, setSopVersionId] = useState(null); // null = always use latest
	const [sopVersions, setSopVersions] = useState([]);
	const [loadingVersions, setLoadingVersions] = useState(false);
	const [requireSopCompletion, setRequireSopCompletion] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [error, setError] = useState(null);
	const [sopQuery, setSopQuery] = useState('');
	const sopComboboxButtonRef = useRef(null);

	// Filter and sort SOPs based on search query
	const filteredSops = useMemo(() => {
		const sorted = [...sops].sort((a, b) =>
			(a.name || '').localeCompare(b.name || '', undefined, {
				numeric: true,
				sensitivity: 'base',
			}),
		);
		if (!sopQuery.trim()) return sorted;
		const query = sopQuery.toLowerCase();
		return sorted.filter(
			(sop) =>
				sop.name?.toLowerCase().includes(query) ||
				sop.description?.toLowerCase().includes(query),
		);
	}, [sops, sopQuery]);

	// Reset form when modal opens
	useEffect(() => {
		if (open) {
			setTitle('');
			setContent('');
			setAssignedTo(null);
			setDueDate('');
			setRecurrence(null);
			setRecurrenceEndDate('');
			setReminderOffset(null);
			setSopId(null);
			setSopVersionId(null);
			setSopVersions([]);
			setRequireSopCompletion(false);
			setError(null);
			setSopQuery('');
		}
	}, [open]);

	// Fetch SOP versions when SOP is selected
	useEffect(() => {
		if (sopId) {
			setLoadingVersions(true);
			setSopVersionId(null); // Reset version selection
			getSopVersions({ id: sopId })
				.then((versions) => {
					setSopVersions(versions || []);
				})
				.catch((err) => {
					console.error('Failed to load SOP versions:', err);
					setSopVersions([]);
				})
				.finally(() => {
					setLoadingVersions(false);
				});
		} else {
			setSopVersions([]);
			setSopVersionId(null);
		}
	}, [sopId]);

	const handleSubmit = async (e) => {
		e.preventDefault();

		if (!title.trim()) {
			setError('Task title is required');
			return;
		}

		setIsSubmitting(true);
		setError(null);

		try {
			if (recurrence) {
				// Create a recurrence rule (which also creates the first task)
				await createRecurrenceRule({
					title: title.trim() || null,
					content: content.trim() || null,
					assignedTo: assignedTo || null,
					startDate: dueDate || null,
					recurrence,
					recurrenceEndDate: recurrenceEndDate || null,
					reminderOffset: dueDate ? reminderOffset : null,
					sopId: sopId || null,
					sopVersionId: sopId ? sopVersionId : null, // null = always use latest
					requireSopCompletion: sopId ? requireSopCompletion : false,
				});
			} else {
				// Create a one-time task
				await createTask({
					title: title.trim() || null,
					content: content.trim() || null,
					assignedTo: assignedTo || null,
					dueDate: dueDate || null,
					reminderOffset: dueDate ? reminderOffset : null,
					sopId: sopId || null,
					sopVersionId: sopId ? sopVersionId : null, // null = always use latest
					requireSopCompletion: sopId ? requireSopCompletion : false,
				});
			}

			if (onSuccess) {
				onSuccess();
			}
			setClose();
		} catch (err) {
			setError(err.message || 'Failed to create task');
		} finally {
			setIsSubmitting(false);
		}
	};

	const selectedAssignee = team.find((m) => m.id === assignedTo);
	const selectedRecurrence = RECURRENCE_OPTIONS.find(
		(r) => r.value === recurrence,
	);
	const selectedReminder = REMINDER_OPTIONS.find(
		(r) => r.value === reminderOffset,
	);
	const selectedSop = sops.find((s) => s.id === sopId);

	return (
		<StackableModal open={!!open} onClose={() => setClose()} size="md">
			<form onSubmit={handleSubmit} className="space-y-4">
				<div>
					<h2 className="text-lg font-semibold text-gray-900">Create Task</h2>
					<p className="text-sm text-gray-500 mt-1">
						Add a new task to track work that needs to be done.
					</p>
				</div>

				{error && (
					<div className="rounded-md bg-red-50 p-3">
						<p className="text-sm text-red-700">{error}</p>
					</div>
				)}

				{/* Task Title */}
				<div>
					<label
						htmlFor="title"
						className="block text-sm font-medium text-gray-700"
					>
						Title <span className="text-red-500">*</span>
					</label>
					<input
						type="text"
						id="title"
						value={title}
						onChange={(e) => setTitle(e.target.value)}
						placeholder="Task title"
						className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
						autoFocus
					/>
				</div>

				{/* Task Description */}
				<div>
					<label
						htmlFor="content"
						className="block text-sm font-medium text-gray-700"
					>
						Description
					</label>
					<textarea
						id="content"
						value={content}
						onChange={(e) => setContent(e.target.value)}
						placeholder="Add details about this task..."
						rows={3}
						className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
					/>
				</div>

				{/* Assign To */}
				<div>
					<label className="block text-sm font-medium text-gray-700 mb-1">
						Assign to
					</label>
					<Listbox value={assignedTo} onChange={setAssignedTo}>
						<div className="relative">
							<Listbox.Button className="relative w-full cursor-pointer rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-left text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500">
								{selectedAssignee ? (
									<div className="flex items-center gap-2">
										<AvatarGenerated
											id={getUserFullName(selectedAssignee)}
											size="xxs"
										/>
										<span>{getUserFullName(selectedAssignee)}</span>
									</div>
								) : (
									<span className="text-gray-500">Unassigned</span>
								)}
								<ChevronUpDownIcon className="absolute right-2 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
							</Listbox.Button>
							<Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
								<Listbox.Option
									value={null}
									className={({ active }) =>
										classNames(
											'cursor-pointer select-none relative py-2 pl-3 pr-9',
											active ? 'bg-indigo-50' : '',
										)
									}
								>
									<span className="text-gray-500">Unassigned</span>
								</Listbox.Option>
								{team
									?.filter((m) => m.active && !m.archived)
									?.map((member) => (
										<Listbox.Option
											key={member.id}
											value={member.id}
											className={({ active, selected }) =>
												classNames(
													'cursor-pointer select-none relative py-2 pl-3 pr-9',
													active ? 'bg-indigo-50' : '',
													selected ? 'bg-indigo-100' : '',
												)
											}
										>
											{({ selected }) => (
												<div className="flex items-center gap-2">
													<AvatarGenerated
														id={getUserFullName(member)}
														size="xxs"
													/>
													<span className={selected ? 'font-semibold' : ''}>
														{getUserFullName(member)}
													</span>
													{selected && (
														<CheckIcon className="h-4 w-4 text-indigo-600 ml-auto" />
													)}
												</div>
											)}
										</Listbox.Option>
									))}
							</Listbox.Options>
						</div>
					</Listbox>
				</div>

				{/* Due Date */}
				<div>
					<label className="block text-sm font-medium text-gray-700 mb-1">
						Due date
					</label>
					<DatePicker
						value={dueDate}
						onChange={setDueDate}
						placeholder="Select due date"
						className="w-full"
					/>
				</div>

				{/* Recurrence */}
				<div>
					<label className="block text-sm font-medium text-gray-700 mb-1">
						Recurrence
					</label>
					<Listbox value={recurrence} onChange={setRecurrence}>
						<div className="relative">
							<Listbox.Button className="relative w-full cursor-pointer rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-left text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500">
								<span
									className={selectedRecurrence?.value ? '' : 'text-gray-500'}
								>
									{selectedRecurrence?.label || 'No recurrence'}
								</span>
								<ChevronUpDownIcon className="absolute right-2 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
							</Listbox.Button>
							<Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
								{RECURRENCE_OPTIONS.map((option) => (
									<Listbox.Option
										key={option.value || 'none'}
										value={option.value}
										className={({ active, selected }) =>
											classNames(
												'cursor-pointer select-none relative py-2 pl-3 pr-9',
												active ? 'bg-indigo-50' : '',
												selected ? 'bg-indigo-100' : '',
											)
										}
									>
										{({ selected }) => (
											<div className="flex items-center justify-between">
												<span className={selected ? 'font-semibold' : ''}>
													{option.label}
												</span>
												{selected && (
													<CheckIcon className="h-4 w-4 text-indigo-600" />
												)}
											</div>
										)}
									</Listbox.Option>
								))}
							</Listbox.Options>
						</div>
					</Listbox>

					{/* Recurrence End Date - only show if recurrence is selected */}
					{recurrence && (
						<div className="mt-2">
							<label className="block text-xs text-gray-500 mb-1">
								End recurrence on (optional)
							</label>
							<DatePicker
								value={recurrenceEndDate}
								onChange={setRecurrenceEndDate}
								placeholder="No end date"
								className="w-full"
							/>
						</div>
					)}
				</div>

				{/* Reminder - only show if due date is set */}
				{dueDate && (
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-1">
							Due date reminder
						</label>
						<Listbox value={reminderOffset} onChange={setReminderOffset}>
							<div className="relative">
								<Listbox.Button className="relative w-full cursor-pointer rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-left text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500">
									<span
										className={selectedReminder?.value ? '' : 'text-gray-500'}
									>
										{selectedReminder?.label || 'No reminder'}
									</span>
									<ChevronUpDownIcon className="absolute right-2 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
								</Listbox.Button>
								<Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
									{REMINDER_OPTIONS.map((option) => (
										<Listbox.Option
											key={option.value || 'none'}
											value={option.value}
											className={({ active, selected }) =>
												classNames(
													'cursor-pointer select-none relative py-2 pl-3 pr-9',
													active ? 'bg-indigo-50' : '',
													selected ? 'bg-indigo-100' : '',
												)
											}
										>
											{({ selected }) => (
												<div className="flex items-center justify-between">
													<span className={selected ? 'font-semibold' : ''}>
														{option.label}
													</span>
													{selected && (
														<CheckIcon className="h-4 w-4 text-indigo-600" />
													)}
												</div>
											)}
										</Listbox.Option>
									))}
								</Listbox.Options>
							</div>
						</Listbox>
					</div>
				)}

				{/* SOP - only show if SOPs are available */}
				{sops.length > 0 && (
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-1">
							Standard Operating Procedure
						</label>
						<Combobox
							value={sopId}
							onChange={(value) => {
								setSopId(value);
								setSopQuery('');
							}}
						>
							<div className="relative">
								<div className="relative w-full cursor-default overflow-hidden rounded-md border border-gray-300 bg-white text-left focus-within:border-indigo-500 focus-within:ring-1 focus-within:ring-indigo-500">
									<MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
									<Combobox.Input
										className="w-full border-none py-2 pl-9 pr-10 text-sm leading-5 text-gray-900 focus:ring-0 focus:outline-none"
										placeholder={selectedSop?.name || 'Search SOPs...'}
										displayValue={() => (sopQuery ? sopQuery : '')}
										onChange={(e) => setSopQuery(e.target.value)}
										onFocus={(e) => {
											if (selectedSop) {
												e.target.placeholder = selectedSop.name;
											}
											// Open dropdown on focus
											sopComboboxButtonRef.current?.click();
										}}
									/>
									<Combobox.Button
										ref={sopComboboxButtonRef}
										className="absolute inset-y-0 right-0 flex items-center pr-2"
									>
										<ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
									</Combobox.Button>
								</div>
								<Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
									{selectedSop && (
										<Combobox.Option
											value={null}
											className={({ active }) =>
												classNames(
													'cursor-pointer select-none relative py-2 pl-3 pr-9',
													active ? 'bg-indigo-50' : '',
												)
											}
										>
											<span className="text-gray-500">Clear selection</span>
										</Combobox.Option>
									)}
									{filteredSops.length === 0 && sopQuery !== '' ? (
										<div className="relative cursor-default select-none py-2 px-4 text-gray-500">
											No SOPs found.
										</div>
									) : (
										filteredSops.map((sop) => (
											<Combobox.Option
												key={sop.id}
												value={sop.id}
												className={({ active, selected }) =>
													classNames(
														'cursor-pointer select-none relative py-2 pl-3 pr-9',
														active ? 'bg-indigo-50' : '',
														selected ? 'bg-indigo-100' : '',
													)
												}
											>
												{({ selected }) => (
													<div>
														<div className="flex items-center justify-between">
															<span
																className={selected ? 'font-semibold' : ''}
															>
																{sop.name}
															</span>
															{selected && (
																<CheckIcon className="h-4 w-4 text-indigo-600" />
															)}
														</div>
														<p className="text-xs text-gray-500 mt-0.5">
															Latest version:{' '}
															{sop.versionMajor !== undefined &&
															sop.versionMinor !== undefined
																? `v${sop.versionMajor}.${sop.versionMinor}`
																: 'Unpublished'}
														</p>
													</div>
												)}
											</Combobox.Option>
										))
									)}
								</Combobox.Options>
							</div>
						</Combobox>

						{/* Version Selection - only show when SOP is selected */}
						{sopId && (
							<div className="mt-3">
								<label className="block text-xs text-gray-500 mb-1">
									SOP Version
								</label>
								<Listbox value={sopVersionId} onChange={setSopVersionId}>
									<div className="relative">
										<Listbox.Button className="relative w-full cursor-pointer rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-left text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500">
											{loadingVersions ? (
												<span className="text-gray-400">
													Loading versions...
												</span>
											) : sopVersionId ? (
												<span>
													Version{' '}
													{
														sopVersions.find((v) => v.id === sopVersionId)
															?.versionMajor
													}
													.
													{
														sopVersions.find((v) => v.id === sopVersionId)
															?.versionMinor
													}
												</span>
											) : (
												<span className="text-gray-700">
													Always use latest version
												</span>
											)}
											<ChevronUpDownIcon className="absolute right-2 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
										</Listbox.Button>
										<Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
											<Listbox.Option
												value={null}
												className={({ active, selected }) =>
													classNames(
														'cursor-pointer select-none relative py-2 pl-3 pr-9',
														active ? 'bg-indigo-50' : '',
														selected ? 'bg-indigo-100' : '',
													)
												}
											>
												{({ selected }) => (
													<div className="flex items-center justify-between">
														<div>
															<span className={selected ? 'font-semibold' : ''}>
																Always use latest version
															</span>
															<p className="text-xs text-gray-500">
																Task will always use the most recent SOP version
															</p>
														</div>
														{selected && (
															<CheckIcon className="h-4 w-4 text-indigo-600" />
														)}
													</div>
												)}
											</Listbox.Option>
											{sopVersions.map((version) => (
												<Listbox.Option
													key={version.id}
													value={version.id}
													className={({ active, selected }) =>
														classNames(
															'cursor-pointer select-none relative py-2 pl-3 pr-9',
															active ? 'bg-indigo-50' : '',
															selected ? 'bg-indigo-100' : '',
														)
													}
												>
													{({ selected }) => (
														<div className="flex items-center justify-between">
															<div>
																<span
																	className={selected ? 'font-semibold' : ''}
																>
																	Version {version.versionMajor}.
																	{version.versionMinor}
																</span>
																{version.publishedAt && (
																	<p className="text-xs text-gray-500">
																		Published{' '}
																		{new Date(
																			version.publishedAt,
																		).toLocaleDateString()}
																	</p>
																)}
															</div>
															{selected && (
																<CheckIcon className="h-4 w-4 text-indigo-600" />
															)}
														</div>
													)}
												</Listbox.Option>
											))}
										</Listbox.Options>
									</div>
								</Listbox>
							</div>
						)}

						{/* Require SOP Completion checkbox - only show when SOP is selected */}
						{sopId && (
							<div className="mt-3">
								<label className="flex items-center gap-2 cursor-pointer">
									<input
										type="checkbox"
										checked={requireSopCompletion}
										onChange={(e) => setRequireSopCompletion(e.target.checked)}
										className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
									/>
									<span className="text-sm text-gray-700 ml-1.5">
										Require SOP completion before task can be completed
									</span>
								</label>
							</div>
						)}
					</div>
				)}

				{/* Actions */}
				<div className="flex justify-end gap-3 pt-4">
					<button
						type="button"
						onClick={() => setClose()}
						className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
					>
						Cancel
					</button>
					<button
						type="submit"
						disabled={isSubmitting || !title.trim()}
						className={classNames(
							'px-4 py-2 text-sm font-medium text-white rounded-md',
							isSubmitting || !title.trim()
								? 'bg-indigo-400 cursor-not-allowed'
								: 'bg-indigo-600 hover:bg-indigo-700',
						)}
					>
						{isSubmitting ? 'Creating...' : 'Create Task'}
					</button>
				</div>
			</form>
		</StackableModal>
	);
};

export default CreateTaskModal;
